// components/common/Dropdown.tsx
import React, { useEffect, useRef, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import { Button } from "@/components/common/Button";
import Loader from "./Loader";
import { Plus } from "lucide-react";

type DropdownItem = {
  id: string;
  label: string;
  color?: string;
};

interface DropdownProps {
  button: React.ReactElement;
  items: DropdownItem[];
  selected: string[];
  onChange: (selected: string[]) => void;
  onSearch?: (query: string) => Promise<DropdownItem[]>;
  withCheckboxes?: boolean;
  withSelectAll?: boolean;
  closeOnSelect?: boolean;
  widthPx?: number;
  loading?: boolean;
  onAddClick?: () => void;
  addLabel?: string;
  showSearchBar?: boolean;
  showAddButton?: boolean;
  treatEmptyAsAll?: boolean;
}

export const Dropdown: React.FC<DropdownProps> = ({
  button,
  items,
  selected,
  onChange,
  onSearch,
  withCheckboxes = false,
  withSelectAll = false,
  closeOnSelect = false,
  widthPx = 220,
  loading = false,
  onAddClick,
  addLabel,
  showSearchBar,
  showAddButton,
  treatEmptyAsAll = false,
}) => {
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const selectAllRef = useRef<HTMLInputElement | null>(null);
  const [open, setOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [localSearchTerm, setLocalSearchTerm] = useState("");
  const [filteredItems, setFilteredItems] = useState(items);
  const [pos, setPos] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  const capitalize = (str: string) => {
    if (!str) return "";
    const lowerStr = String(str).toLowerCase();
    return lowerStr.charAt(0).toUpperCase() + lowerStr.slice(1);
  };

  // Filter items based on search term
  useEffect(() => {
    if (!localSearchTerm || localSearchTerm.trim() === "") {
      setFilteredItems(items);
      return;
    }

    if (onSearch) {
      const searchTimeout = setTimeout(() => {
        setSearchLoading(true);
        onSearch(localSearchTerm.trim())
          .then((results) => {
            setFilteredItems(results);
            setSearchLoading(false);
          })
          .catch((error) => {
            console.error("Search error:", error);
            setSearchLoading(false);
            const filtered = items.filter((item) =>
              item.label.toLowerCase().includes(localSearchTerm.toLowerCase())
            );
            setFilteredItems(filtered);
          });
      }, 300);

      return () => clearTimeout(searchTimeout);
    } else {
      const filtered = items.filter((item) =>
        item.label.toLowerCase().includes(localSearchTerm.toLowerCase())
      );
      setFilteredItems(filtered);
    }
  }, [localSearchTerm, items, onSearch]);

  // Reset when dropdown closes
  useEffect(() => {
    if (!open) {
      setLocalSearchTerm("");
      setFilteredItems(items);
    }
  }, [open, items]);

  // Calculate select all state
  const getSelectAllState = useCallback(() => {
    if (filteredItems.length === 0) {
      return { isChecked: false, isIndeterminate: false };
    }

    const filteredIds = filteredItems.map(item => item.id);
    const selectedFilteredItems = selected.filter(id => filteredIds.includes(id));

    if (treatEmptyAsAll) {
      // With API approach: empty array means all items are shown (no filter)
      if (selected.length === 0) {
        // No filter = all items shown = checkbox checked
        return { isChecked: true, isIndeterminate: false };
      }
      
      // Check if ALL filtered items are selected
      if (selectedFilteredItems.length === filteredIds.length && selectedFilteredItems.length === selected.length) {
        return { isChecked: true, isIndeterminate: false };
      }
      
      // Some items selected
      if (selectedFilteredItems.length > 0) {
        return { isChecked: false, isIndeterminate: true };
      }
      
      return { isChecked: false, isIndeterminate: false };
    } else {
      // Standard behavior
      const isChecked = selectedFilteredItems.length === filteredItems.length && selectedFilteredItems.length > 0;
      const isIndeterminate = selectedFilteredItems.length > 0 && selectedFilteredItems.length < filteredItems.length;
      return { isChecked, isIndeterminate };
    }
  }, [selected, filteredItems, treatEmptyAsAll]);

  const { isChecked, isIndeterminate } = getSelectAllState();

  // Set indeterminate state on checkbox ref
  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.checked = isChecked;
      selectAllRef.current.indeterminate = isIndeterminate;
    }
  }, [isChecked, isIndeterminate]);

  // Toggle all items - FIXED LOGIC
  const toggleAll = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const filteredIds = filteredItems.map((item) => item.id);
      
      if (treatEmptyAsAll) {
        // API approach: empty = all selected
        if (isChecked) {
          // Currently all selected, deselect all
          // To show some selection in UI, select just the first item
          if (filteredIds.length > 0) {
            onChange([filteredIds[0]]);
          } else {
            onChange([]);
          }
        } else {
          // Currently none or partial selected, select all
          // In API terms, this means clear the filter (empty array)
          onChange([]);
        }
      } else {
        // Standard approach
        if (isChecked) {
          // Deselect all filtered items
          const newSelected = selected.filter(id => !filteredIds.includes(id));
          onChange(newSelected);
        } else {
          // Select all filtered items
          const existingOtherSelections = selected.filter(id => !filteredIds.includes(id));
          const newSelected = [...existingOtherSelections, ...filteredIds];
          onChange(newSelected);
        }
      }
    },
    [selected, filteredItems, onChange, treatEmptyAsAll, isChecked]
  );

  // Toggle single item
  const toggleItem = useCallback(
    (id: string) => {
      if (treatEmptyAsAll) {
        // If currently showing all (empty), we need to build the selection
        if (selected.length === 0) {
          // Currently showing all, user is deselecting one item
          // So select all items EXCEPT the clicked one
          const allIds = items.map(item => item.id);
          const newSelected = allIds.filter(itemId => itemId !== id);
          onChange(newSelected);
        } else {
          // Normal toggle
          if (selected.includes(id)) {
            const newSelected = selected.filter((s) => s !== id);
            // If deselecting would result in selecting all items, just clear
            if (newSelected.length === items.length - 1) {
              onChange([]);
            } else {
              onChange(newSelected);
            }
          } else {
            const newSelected = [...selected, id];
            // If selecting results in all items selected, just clear (show all)
            if (newSelected.length === items.length) {
              onChange([]);
            } else {
              onChange(newSelected);
            }
          }
        }
      } else {
        // Standard toggle
        if (selected.includes(id)) {
          const newSelected = selected.filter((s) => s !== id);
          onChange(newSelected);
        } else {
          const newSelected = [...selected, id];
          onChange(newSelected);
        }
      }
    },
    [selected, onChange, items, treatEmptyAsAll]
  );

  // Dropdown positioning and event handlers
  const updatePosition = useCallback(() => {
    const trig = triggerRef.current;
    if (!trig) return;
    const r = trig.getBoundingClientRect();
    const leftCandidate = r.left + window.scrollX;
    const topCandidate = r.bottom + window.scrollY;
    const viewportW = document.documentElement.clientWidth;
    const left = Math.min(
      Math.max(8, leftCandidate),
      Math.max(8, viewportW - widthPx - 8)
    );
    setPos({ top: topCandidate, left });
  }, [widthPx]);

  useEffect(() => {
    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (triggerRef.current?.contains(target)) return;
      if (menuRef.current && menuRef.current.contains(target)) return;
      setOpen(false);
    };

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };

    window.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("keydown", handleKey);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("keydown", handleKey);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [updatePosition]);

  useEffect(() => {
    if (open) updatePosition();
  }, [open, items, updatePosition]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalSearchTerm(e.target.value);
  };

  // UI rendering
  const menu = (
    <div
      ref={menuRef}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: "absolute",
        top: pos.top,
        left: pos.left,
        width: widthPx,
        maxWidth: `min(${widthPx}px, calc(100vw - 16px))`,
      }}
      className="bg-white border rounded-lg shadow-lg z-[9999] mt-2"
    >
      {/* Top row: Search and Add button */}
      {(showSearchBar || showAddButton) && (
        <div className="flex items-center gap-2 mb-2 pt-2 px-2">
          {showSearchBar && (
            <input
              type="text"
              value={localSearchTerm}
              onChange={handleSearchChange}
              placeholder={`Search ${addLabel?.toLowerCase() || "items"}...`}
              className="flex-1 min-w-0 px-2 py-1 border rounded-lg text-xs"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              autoFocus={false}
            />
          )}
          {showAddButton && onAddClick && (
            <Button
              variant="primary"
              size="xs"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onAddClick();
              }}
                      className={`flex-shrink-0 whitespace-nowrap h-6 ${
                addLabel === "Add Status" ? "w-full" : ""
              }`}
            >
              <Plus size={18} /> &nbsp; {addLabel}
            </Button>
          )}
        </div>
      )}

      {/* Select All */}
      {withSelectAll && withCheckboxes && (
        <div className="mb-1 border-b py-1 border-t p-2">
          <div
            className="flex items-center px-2 py-1 cursor-pointer hover:bg-gray-50 rounded select-none outline-none focus:outline-none"
            onClick={toggleAll}
            onMouseDown={(e) => e.preventDefault()}
          >
            <input
              ref={selectAllRef}
              type="checkbox"
              className="h-4 w-4 border-gray-300 rounded pointer-events-none outline-none focus:outline-none"
              checked={isChecked}
              readOnly
              tabIndex={-1}
              style={{ accentColor: "#2563eb" }}
              disabled={loading || searchLoading || filteredItems.length === 0}
            />
            <span className="ml-2 text-xs font-medium">
              Select all{" "}
              {/* {filteredItems.length > 0 &&
                `(${
                  treatEmptyAsAll && selected.length === 0
                    ? filteredItems.length
                    : selected.filter((id) =>
                        filteredItems.some((item) => item.id === id)
                      ).length
                }/${filteredItems.length})`} */}
            </span>
          </div>
        </div>
      )}

      {/* List or loader */}
      {loading || searchLoading ? (
        <div className="flex justify-center items-center h-20">
          <Loader />
        </div>
      ) : (
        <div className="max-h-[240px] overflow-y-auto px-2 pb-2">
          {filteredItems.length === 0 ? (
            <div className="text-center text-gray-500 text-xs py-4">
              No items found
            </div>
          ) : (
            filteredItems.map((it) => {
              // Determine if item is selected
              let isSelected: boolean;
              if (treatEmptyAsAll && selected.length === 0) {
                // Empty means all selected
                isSelected = true;
              } else {
                isSelected = selected.includes(it.id);
              }
              
              return (
                <div
                  key={it.id}
                  className="flex items-center px-2 py-1 cursor-pointer hover:bg-gray-50 rounded select-none"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (withCheckboxes) {
                      toggleItem(it.id);
                    } else if (closeOnSelect) {
                      onChange([it.id]);
                      setOpen(false);
                    }
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  {withCheckboxes && (
                    <input
                      type="checkbox"
                      className="h-4 w-4 border-gray-300 rounded pointer-events-none"
                      checked={isSelected}
                      readOnly
                      style={{ accentColor: "#2563eb" }}
                      disabled={loading}
                    />
                  )}
                  {addLabel === "Add Status" && it.color && (
                    <span
                      className="ml-2 h-3 w-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: it.color }}
                    />
                  )}
                  <span className="ml-2 text-xs truncate">
                    {it.label ? capitalize(it.label) : "Action"}
                  </span>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );

  // Render trigger and portal
  const mergedButton = React.cloneElement(button, {
    onClick: (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (button.props.onClick) button.props.onClick(e);
      setOpen((o) => {
        const next = !o;
        if (next) setTimeout(updatePosition, 0);
        return next;
      });
    },
    ref: (node: HTMLButtonElement | null) => {
      const origRef = (button as any).ref;
      if (typeof origRef === "function") origRef(node);
      else if (origRef && typeof origRef === "object")
        (origRef as any).current = node;
    },
  });

  return (
    <div ref={triggerRef} className="inline-block relative">
      {mergedButton}
      {open &&
        typeof document !== "undefined" &&
        ReactDOM.createPortal(menu, document.body)}
    </div>
  );
};